import { useCallback, useMemo, useState } from "react";
import { Card, EmptyState, useEntityDetail } from "@pericles/framework-frontend";

import {
  generatePreDiscoveryQuestions,
  updatePreDiscoveryQuestionAnswers,
} from "../api/projectPreDiscoveryClient";
import type { ProjectDetail } from "../api/projectsClient";

import { ContextUsedBlock } from "./ContextUsedBlock";
import { ProjectDiscoveryDocumentsPanel } from "./ProjectDiscoveryDocumentsPanel";
import { ProjectDiscoveryConversationsPanel } from "./ProjectDiscoveryConversationsPanel";
import { ProjectDiscoveryNotesPanel } from "./ProjectDiscoveryNotesPanel";
import { ProjectMeetingsTab } from "./ProjectMeetingsTab";

interface ProjectPreDiscoveryPanelProps {
  projectId: string;
  contextRefreshKey?: string | number;
  onQuestionsUpdated?: () => void;
}

type QuestionWithLocalId = {
  __id: string;
  id?: string;
  theme?: string;
  question?: string;
  why_it_matters?: string;
  what_good_answer_looks_like?: string;
  priority?: string;
  owner?: string;
  answer?: string;
  followups?: string[];
};

function groupByTheme(questions: QuestionWithLocalId[]): Map<string, QuestionWithLocalId[]> {
  const grouped = new Map<string, QuestionWithLocalId[]>();
  questions.forEach((question) => {
    const theme = (question?.theme as string | undefined)?.trim() || "other";
    if (!grouped.has(theme)) {
      grouped.set(theme, []);
    }
    grouped.get(theme)?.push(question);
  });
  return grouped;
}

export function ProjectPreDiscoveryPanel({
  projectId,
  contextRefreshKey,
  onQuestionsUpdated,
}: ProjectPreDiscoveryPanelProps): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useEntityDetail<ProjectDetail>({
    entity: "Project",
    id: projectId,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingAnswerId, setIsSavingAnswerId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [contextModuleRefreshKey, setContextModuleRefreshKey] = useState(0);
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({});

  const questions = useMemo(() => {
    if (!data?.preDiscoveryQuestions) return [];
    if (Array.isArray(data.preDiscoveryQuestions)) return data.preDiscoveryQuestions as any[];
    return [];
  }, [data?.preDiscoveryQuestions]);

  const questionsWithIds = useMemo(
    () =>
      questions.map((question: any, index) => ({
        ...question,
        __id:
          typeof question?.id === "string" && question.id.trim()
            ? question.id
            : `${String(question?.theme ?? "other").toLowerCase()}-${index}`,
      })) as QuestionWithLocalId[],
    [questions],
  );

  const groupedQuestions = useMemo(() => groupByTheme(questionsWithIds), [questionsWithIds]);

  const readAnswerValue = useCallback(
    (question: QuestionWithLocalId): string => {
      if (question.__id in answerDrafts) {
        return answerDrafts[question.__id] ?? "";
      }
      return typeof question.answer === "string" ? question.answer : "";
    },
    [answerDrafts],
  );

  const handleGenerate = async () => {
    setIsGenerating(true);
    setMessage(null);
    try {
      const response = await generatePreDiscoveryQuestions(projectId);
      setMessage(`Generated ${response.questions.length} questions with ${response.model}.`);
      onQuestionsUpdated?.();
      await refetch();
    } catch (generationError: any) {
      setMessage(generationError instanceof Error ? generationError.message : "Failed to generate questions");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswerChange = useCallback((key: string, value: string) => {
    setMessage(null);
    setAnswerDrafts((current) => ({ ...current, [key]: value }));
  }, []);

  const handleSaveAnswer = useCallback(
    async (question: QuestionWithLocalId) => {
      const value = readAnswerValue(question);
      setIsSavingAnswerId(question.__id);
      setMessage(null);
      try {
        await updatePreDiscoveryQuestionAnswers(projectId, [
          {
            id: typeof question.id === "string" ? question.id : undefined,
            question: typeof question.question === "string" ? question.question : undefined,
            answer: value ?? "",
            answeredBy: "client",
          },
        ]);
        setMessage("Answer saved.");
        setContextModuleRefreshKey((current) => current + 1);
        onQuestionsUpdated?.();
        await refetch();
      } catch (saveError) {
        setMessage(saveError instanceof Error ? saveError.message : "Failed to save answer");
      } finally {
        setIsSavingAnswerId(null);
      }
    },
    [onQuestionsUpdated, projectId, readAnswerValue, refetch],
  );

  const handleCopy = async () => {
    if (!questionsWithIds.length || typeof navigator?.clipboard?.writeText !== "function") return;
    const text = questionsWithIds
      .map(
        (q) =>
          `${q.theme ? `[${q.theme.toUpperCase()}] ` : ""}${q.question}\n- Why: ${q.why_it_matters}\n- Good answer: ${q.what_good_answer_looks_like}\n- Response: ${readAnswerValue(q) || "[pending]"}`,
      )
      .join("\n\n");
    await navigator.clipboard.writeText(text);
    setMessage("Copied questions to clipboard.");
  };

  const derivedRefreshKey = useMemo(() => {
    if (contextRefreshKey !== undefined) return contextRefreshKey;
    return [
      data?.contextGenerationStatus,
      data?.contextGeneratedAt,
      data?.preDiscoveryQuestionsStatus,
      data?.preDiscoveryQuestionsGeneratedAt,
      contextModuleRefreshKey,
      typeof data?.customer === "object" && data?.customer !== null
        ? (data.customer as any).contextGenerationStatus
        : null,
      typeof data?.customer === "object" && data?.customer !== null
        ? (data.customer as any).contextGeneratedAt
        : null,
    ]
      .filter(Boolean)
      .join("|");
  }, [
    contextRefreshKey,
    contextModuleRefreshKey,
    data?.contextGeneratedAt,
    data?.contextGenerationStatus,
    data?.preDiscoveryQuestionsGeneratedAt,
    data?.preDiscoveryQuestionsStatus,
    data?.customer,
  ]);

  const handleNotesUpdated = useCallback(() => {
    setNotesRefreshKey((current) => current + 1);
  }, []);

  if (isLoading) {
    return (
      <Card>
        <div className="p-3 text-sm text-slate-700">Loading pre-discovery questions...</div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <div className="p-3 text-sm text-red-600">{error instanceof Error ? error.message : "Failed to load project"}</div>
      </Card>
    );
  }

  if (!data) {
    return <EmptyState title="Project not found" description="The requested project could not be found." />;
  }

  const status = data.preDiscoveryQuestionsStatus ?? "idle";
  const statusLine = `Status: ${status}${data.preDiscoveryQuestionsGeneratedAt ? ` - generated ${new Date(data.preDiscoveryQuestionsGeneratedAt).toLocaleString()}` : ""}`;

  return (
    <div className="flex flex-col gap-4">
      <ContextUsedBlock projectId={projectId} refreshKey={derivedRefreshKey} />

      <div className="flex flex-col gap-3">
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-base font-semibold text-slate-900">Pre-discovery questions</div>
              <p className="text-sm text-slate-600">{statusLine}</p>
              {data.preDiscoveryQuestionsError ? (
                <p className="text-sm text-red-600">{data.preDiscoveryQuestionsError}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <button type="button" className="button button-secondary button-sm" onClick={handleCopy} disabled={!questionsWithIds.length}>
                Copy to clipboard
              </button>
              <button type="button" className="button button-primary button-sm" onClick={handleGenerate} disabled={isGenerating}>
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            </div>
          </div>
          {message ? <p className="mt-2 text-sm text-slate-700">{message}</p> : null}
        </Card>

        {questionsWithIds.length === 0 ? (
          <Card>
            <div className="p-3 text-sm text-slate-700">No pre-discovery questions yet. Use Generate to draft a set.</div>
          </Card>
        ) : (
          Array.from(groupedQuestions.entries()).map(([theme, entries]) => (
            <Card key={theme}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-slate-900">{theme.toUpperCase()}</h3>
                  <span className="text-xs uppercase text-slate-500">{entries.length} questions</span>
                </div>
                <div className="flex flex-col gap-3">
                  {entries.map((question, index) => (
                    <div key={`${theme}-${index}`} className="rounded border border-slate-200 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">{question.question}</p>
                        <div className="flex items-center gap-2">
                          <span className="rounded bg-slate-100 px-2 py-1 text-xs uppercase text-slate-700">{question.priority}</span>
                          <span
                            className={`rounded px-2 py-1 text-[10px] uppercase ${
                              readAnswerValue(question).trim() ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {readAnswerValue(question).trim() ? "answered" : "open"}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-slate-700">Why it matters: {question.why_it_matters}</p>
                      <p className="text-sm text-slate-700">What a good answer looks like: {question.what_good_answer_looks_like}</p>
                      <p className="text-xs text-slate-600">Owner: {question.owner}</p>
                      <div className="mt-2 space-y-2">
                        <label className="flex flex-col gap-1 text-xs text-slate-700">
                          Response
                          <textarea
                            className="min-h-[72px] rounded border border-slate-300 px-2 py-1 text-sm"
                            value={readAnswerValue(question)}
                            onChange={(event) => handleAnswerChange(question.__id, event.target.value)}
                            placeholder="Add your answer to unblock readiness gate."
                          />
                        </label>
                        <button
                          type="button"
                          className="button button-secondary button-sm"
                          onClick={() => void handleSaveAnswer(question)}
                          disabled={isSavingAnswerId === question.__id}
                        >
                          {isSavingAnswerId === question.__id ? "Saving..." : "Save response"}
                        </button>
                      </div>
                      {question.followups && question.followups.length ? (
                        <ul className="mt-1 list-disc pl-5 text-xs text-slate-700">
                          {question.followups.map((followup: string, followupIndex: number) => (
                            <li key={`${theme}-${index}-followup-${followupIndex}`}>{followup}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ProjectDiscoveryDocumentsPanel projectId={projectId} />
        <ProjectDiscoveryConversationsPanel projectId={projectId} onNotesUpdated={handleNotesUpdated} />
        <ProjectDiscoveryNotesPanel projectId={projectId} refreshKey={notesRefreshKey} />
        <ProjectMeetingsTab projectId={projectId} />
      </div>
    </div>
  );
}
